import { Component, ElementRef, HostListener, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TipoTransacao } from '../../../../../enums/TipoTransacao.enum';
import { NotaDTO, VendaFiltrosDTO } from '../../../../../models/nota.model';
import { PeriodoFiltro } from '../../../../../models/dashboard/periodo-filtro.model';
import { NotaService } from '../../../../../services/financeiro/notas.service';
import { Moeda } from '../../../../../enums/moeda.enum';
import { TimbradoDTO } from '../../../../../models/configuracoes/timbrao.model';
import { TimbradoService } from '../../../../../services/configuracoes/timbrado.service';
import { TableColumn } from '../../../../../models/shared/table-column.model';
import { PessoaDTO } from '../../../../../models/clientes/pessoa.model';
import { EntitySelectorConfig } from '../../../entity-selector/entity-selector.interfaces';
import { PermissaoService } from '../../../../../services/permissao/permissao.service';
import { PessoaService } from '../../../../../services/pessoas/pessoas.service';

@Component({
  selector: 'app-pessoas-table',
  templateUrl: './pessoas-table.component.html',
  styleUrls: ['./pessoas-table.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class PessoasTableComponent implements OnInit, OnDestroy {
  @Input() pessoaId: number | undefined;
  @Input() tipoTransacao: TipoTransacao | undefined;
  @Input() actionLabel: string = '';
  @Input() actionRoute: string = '';

  // ViewChild para foco via atalho F2
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  notas: NotaDTO[] = [] as NotaDTO[];
  first = 0;
  rows = 10;
  /** Valor anterior de "rows" usado como guarda no onRowsPerPageChange
   *  para evitar chamadas duplicadas de findNota quando o (ngModelChange)
   *  do dropdown de itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRows: number = 10;
  totalRecords = 0;
  loading = true;
  lastfield = 'id';
  lastSort = 'asc';
  search: string = '';
  // Filtro de periodo (range) aplicado ao cabecalho da listagem.
  // Estrutura: [dataInicial, dataFinal] | null. Quando o usuario limpa, volta para null.
  rangeDates: Date[] | null = null;
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'pessoas-table-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoNota', labelKey: 'pessoas-table.labels.codigo', visible: true, fixed: true, defaultVisible: true },
    { key: 'nomePessoa', labelKey: 'pessoas-table.labels.pessoa', visible: true, fixed: false, defaultVisible: true },
    { key: 'nomeVendedor', labelKey: 'pessoas-table.labels.vendedor', visible: true, fixed: false, defaultVisible: true },
    { key: 'valorTotal', labelKey: 'pessoas-table.labels.total', visible: true, fixed: false, defaultVisible: true },
    { key: 'quantidade', labelKey: 'pessoas-table.labels.quantidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'condicao', labelKey: 'pessoas-table.labels.condicao', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipo', labelKey: 'pessoas-table.labels.tipo', visible: true, fixed: false, defaultVisible: true },
    { key: 'estado', labelKey: 'pessoas-table.labels.estado', visible: true, fixed: false, defaultVisible: true },
    // Situacao da nota: cancelada ou vigente. Ganhou coluna propria porque a busca passou a
    // trazer as canceladas junto com as demais — sem isso elas se misturam sem nenhum sinal.
    { key: 'situacao', labelKey: 'pessoas-table.labels.situacao', visible: true, fixed: false, defaultVisible: true },
    { key: 'dataInclusao', labelKey: 'pessoas-table.labels.inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'pessoas-table.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // =========================================================================
  // Filtros Avancados (apenas para VENDA)
  // =========================================================================

  // Controle da sidebar
  sidebarFiltrosVisible = false;
  filtrosAtivos = false;

  // DTO de filtros server-side
  filtros: VendaFiltrosDTO = {};

  // Campos da sidebar de filtros
  filtroNotaId: string = '';
  filtroNumeroFatura: string = '';
  filtroDataEmissao: Date | undefined;
  filtroObservacao: string = '';

  // Entity Selector - Cliente (filtro)
  filtroClienteSelecionado: PessoaDTO | undefined;
  filtroClienteSelectorConfig: EntitySelectorConfig = {
    categoryFilter: 'categoriaCliente=true',
    placeholder: '',
    showAddButton: false,
    showAddInDropdown: false,
  };

  // Entity Selector - Vendedor (filtro avancado — VENDA)
  filtroVendedorSelecionado: PessoaDTO | undefined;

  // Opcoes de filtro de estado de cancelamento e fatura legal (VENDA)
  // 'todos' = nao filtra; 'sim' = apenas canceladas; 'nao' = apenas vigentes.
  filtroCanceladoOpcao: 'todos' | 'sim' | 'nao' = 'todos';
  filtroFaturaLegalOpcao: 'todos' | 'sim' | 'nao' = 'todos';

  // Opcoes do dropdown de situacao da nota (montadas em loadTranslations).
  situacaoOpcoes: { label: string; value: 'todos' | 'sim' | 'nao' }[] = [];

  // Lista de timbrados filtrados por tipo VENDA (para dropdown de filtro)
  timbradosVenda: { label: string; value: number }[] = [];

  // Texto descritivo do periodo selecionado (ex: "Janeiro 2024")
  periodoTexto: string = '';

  // Dialog de adicionar pessoa (cliente) a partir do entity-selector
  visibleDialogCliente = false;

  // =========================================================================
  // Objetos de traducao (padrao moderno - tipo literal implicito)
  // =========================================================================

  texts = {
    titulo_tabela: '',
    nenhum_registro: '',
    ajuste_filtros: '',
    carregando: '',
    filtros_avancados: '',
    filtros_avancados_subtitulo: '',
  };

  labels = {
    codigo: '', pessoa: '', vendedor: '', total: '',
    quantidade: '', condicao: '', tipo: '', estado: '', situacao: '',
    cancelada: '', vigente: '',
    inclusao: '', acoes: '', itens_por_pagina: '', ticket: '',
    filtro_id_nota: '',
    filtro_numero_fatura: '',
    filtro_emissao: '',
    filtro_cliente: '',
    filtro_observacao: '',
    filtro_situacao: '',
    situacao_todas: '',
  };

  placeholders = {
    pesquisar: '',
    pesquisar_por_periodo: '',
    selecionar_cliente: '',
    id_nota: '',
    numero_fatura: '',
    emissao: '',
    observacao: '',
  };

  botoes = {
    fatura: '',
    buscar: '',
    nova_venda: '',
    nova_compra: '',
    limpar_filtros: '',
    filtrar: '',
  };

  tooltips = {
    ver_fatura: '',
    filtros_avancados: '',
  };

  private readonly condicaoSearchMap: [string, string][] = [
    ['a vista', 'CONTADO'],
    ['vista', 'CONTADO'],
    ['contado', 'CONTADO'],
    ['a prazo', 'CREDITO'],
    ['prazo', 'CREDITO'],
    ['credito', 'CREDITO'],
    ['al contado', 'CONTADO'],
    ['a credito', 'CREDITO'],
  ];

  private readonly estadoSearchMap: [string, string][] = [
    ['realizado', 'EFETIVADO'],
    ['efetivado', 'EFETIVADO'],
    ['devolvido parcial', 'DEVOLVIDO_PARCIAL'],
    ['devuelto parcial', 'DEVOLVIDO_PARCIAL'],
    ['devolvido', 'DEVOLVIDO'],
    ['devuelto', 'DEVOLVIDO'],
  ];

  private readonly tipoNotaSearchMap: [string, string][] = [
    ['eletronica', 'ELETRONICA'],
    ['pre-impressa', 'PRE_IMPRESSA'],
    ['pre impressa', 'PRE_IMPRESSA'],
    ['auto-impressor', 'AUTO_IMPRESSOR'],
    ['auto impressor', 'AUTO_IMPRESSOR'],
    ['venda comum', 'VENDA_COMUM'],
    ['manual', 'MANUAL'],
    ['electronica', 'ELETRONICA'],
    ['preimpresa', 'PRE_IMPRESSA'],
    ['autoimpresor', 'AUTO_IMPRESSOR'],
    ['venta comun', 'VENDA_COMUM'],
    ['ticket', 'VENDA_COMUM'],
  ];

  private timbradoMap: Map<string, TimbradoDTO> = new Map();
  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private notaService: NotaService,
    private timbradoService: TimbradoService,
    private translate: TranslateService,
    private permissaoService: PermissaoService,
    private pessoaService: PessoaService
  ) { }

  // =========================================================================
  // Getter de visibilidade de filtros avancados
  // =========================================================================

  /** Filtros avancados aparecem para vendas e compras */
  get mostrarFiltrosAvancados(): boolean {
    return this.tipoTransacao === TipoTransacao.VENDA
        || this.tipoTransacao === TipoTransacao.COMPRA;
  }

  /** Retorna true se o tipo de transacao e de compra */
  get ehCompra(): boolean {
    return this.tipoTransacao === TipoTransacao.COMPRA
        || this.tipoTransacao === TipoTransacao.DEVOLUCAO_DE_COMPRA;
  }

  /** Filtro de categoria para o entity-selector de pessoa */
  get filtroCategoriaEntitySelector(): string {
    return this.ehCompra ? 'categoriaFornecedor=true' : 'categoriaCliente=true';
  }

  // Atalhos de teclado
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'F2') {
      ev.preventDefault();
      if (!this.isDialogAberto()) {
        setTimeout(() => {
          this.searchInputRef?.nativeElement?.focus();
        }, 100);
      }
      return;
    }
  }

  private isDialogAberto(): boolean {
    return !!document.querySelector('.p-dialog-mask');
  }

  async ngOnInit() {
    // Configurar entity-selector com permissao e categoria dinamica (cliente ou fornecedor)
    const permissaoTipo = this.ehCompra ? 'fornecedor' : 'cliente';
    const podeAdicionar = this.permissaoService.temPermissao(permissaoTipo);
    const placeholderKey = this.ehCompra
      ? 'pessoas-table.placeholders.selecionar_fornecedor'
      : 'pessoas-table.placeholders.selecionar_cliente';
    this.filtroClienteSelectorConfig = {
      categoryFilter: this.filtroCategoriaEntitySelector,
      placeholder: this.translate.instant(placeholderKey),
      showAddButton: false,
      showAddInDropdown: podeAdicionar,
      dialogIcon: 'pi pi-users',
    };

    this.loadColumnPreferences();
    this.loadTranslations();
    this.carregarTimbrados();

    // Quando pessoaId vier via queryParam, pre-popular o filtro avancado de cliente
    if (this.pessoaId) {
      this.carregarPessoaParaFiltro(this.pessoaId);
    }

    await this.findNota(0, this.rows, 'id,asc');

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  /**
   * Busca os dados da pessoa pelo ID e popula o campo de cliente no filtro avancado.
   * Chamado quando pessoaId chega via @Input (queryParam).
   */
  private carregarPessoaParaFiltro(id: number): void {
    this.pessoaService.getById('/pessoas', String(id)).subscribe({
      next: (pessoa) => {
        this.filtroClienteSelecionado = pessoa;
      },
      error: (err) => {
        console.warn('Erro ao carregar pessoa para filtro:', err);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private carregarTimbrados(): void {
    this.timbradoService.get('/timbrado').subscribe({
      next: (res) => {
        this.timbradoMap = new Map();
        (res?.content ?? []).forEach((t: TimbradoDTO) => {
          if (t?.timbrado) {
            this.timbradoMap.set(String(t.timbrado), t);
          }
        });
      },
      error: (err) => {
        console.warn('Erro ao carregar timbrados para formatação da listagem:', err);
      }
    });
  }

  formatarNumeroTimbradoNota(nota: NotaDTO): string {
    if (!nota?.numeroTimbrado || !nota?.numeroFatura) return '';

    const timbrado = this.timbradoMap.get(String(nota.numeroTimbrado));
    if (!timbrado) return '';

    const filialFormatada = String(timbrado.numeroFilial ?? '').padStart(3, '0');
    const expedicaoFormatada = String(timbrado.pontoDeExpedicao ?? '').padStart(3, '0');
    const numeroFaturaFormatado = String(nota.numeroFatura).padStart(7, '0');

    return `${filialFormatada}-${expedicaoFormatada}-${numeroFaturaFormatado}`;
  }

  get cardTitleKey(): string {
    switch (this.tipoTransacao) {
      case TipoTransacao.VENDA: return 'pessoas-table.texts.lista_de_vendas';
      case TipoTransacao.COMPRA: return 'pessoas-table.texts.lista_de_compras';
      case TipoTransacao.DEVOLUCAO: return 'pessoas-table.texts.lista_de_devolucoes_vendas';
      case TipoTransacao.DEVOLUCAO_DE_COMPRA: return 'pessoas-table.texts.lista_de_devolucoes_compras';
      default: return 'pessoas-table.texts.lista_de_notas';
    }
  }

  get cardIcon(): string {
    switch (this.tipoTransacao) {
      case TipoTransacao.VENDA: return 'pi-shopping-cart';
      case TipoTransacao.COMPRA: return 'pi-shopping-bag';
      case TipoTransacao.DEVOLUCAO:
      case TipoTransacao.DEVOLUCAO_DE_COMPRA: return 'pi-undo';
      default: return 'pi-file';
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  private loadTranslations(): void {
    this.texts = {
      titulo_tabela: this.translate.instant(this.cardTitleKey),
      nenhum_registro: this.translate.instant('pessoas-table.texts.nenhum_registro'),
      ajuste_filtros: this.translate.instant('pessoas-table.texts.ajuste_filtros'),
      carregando: this.translate.instant('pessoas-table.texts.carregando'),
      filtros_avancados: this.translate.instant('pessoas-table.texts.filtros_avancados'),
      filtros_avancados_subtitulo: this.translate.instant(
        this.ehCompra ? 'pessoas-table.texts.filtros_avancados_subtitulo_compras' : 'pessoas-table.texts.filtros_avancados_subtitulo'
      ),
    };

    this.labels = {
      codigo: this.translate.instant('pessoas-table.labels.codigo'),
      pessoa: this.translate.instant('pessoas-table.labels.pessoa'),
      vendedor: this.translate.instant('pessoas-table.labels.vendedor'),
      total: this.translate.instant('pessoas-table.labels.total'),
      quantidade: this.translate.instant('pessoas-table.labels.quantidade'),
      condicao: this.translate.instant('pessoas-table.labels.condicao'),
      tipo: this.translate.instant('pessoas-table.labels.tipo'),
      estado: this.translate.instant('pessoas-table.labels.estado'),
      situacao: this.translate.instant('pessoas-table.labels.situacao'),
      cancelada: this.translate.instant('pessoas-table.labels.cancelada'),
      vigente: this.translate.instant('pessoas-table.labels.vigente'),
      inclusao: this.translate.instant('pessoas-table.labels.inclusao'),
      acoes: this.translate.instant('pessoas-table.labels.acoes'),
      itens_por_pagina: this.translate.instant('pessoas-table.labels.itens_por_pagina'),
      ticket: this.translate.instant('pessoas-table.labels.ticket'),
      filtro_id_nota: this.translate.instant('pessoas-table.labels.filtro_id_nota'),
      filtro_numero_fatura: this.translate.instant('pessoas-table.labels.filtro_numero_fatura'),
      filtro_emissao: this.translate.instant('pessoas-table.labels.filtro_emissao'),
      filtro_cliente: this.translate.instant(
        this.ehCompra ? 'pessoas-table.labels.filtro_fornecedor' : 'pessoas-table.labels.filtro_cliente'
      ),
      filtro_observacao: this.translate.instant('pessoas-table.labels.filtro_observacao'),
      filtro_situacao: this.translate.instant('pessoas-table.labels.filtro_situacao'),
      situacao_todas: this.translate.instant('pessoas-table.labels.situacao_todas'),
    };

    // Dropdown de situacao: "Todas" mantem o comportamento atual (vigentes + canceladas).
    this.situacaoOpcoes = [
      { label: this.labels.situacao_todas, value: 'todos' },
      { label: this.labels.vigente, value: 'nao' },
      { label: this.labels.cancelada, value: 'sim' },
    ];

    this.placeholders = {
      pesquisar: this.translate.instant('pessoas-table.placeholders.pesquisar'),
      pesquisar_por_periodo: this.translate.instant('pessoas-table.placeholders.pesquisar_por_periodo'),
      selecionar_cliente: this.translate.instant(
        this.ehCompra ? 'pessoas-table.placeholders.selecionar_fornecedor' : 'pessoas-table.placeholders.selecionar_cliente'
      ),
      id_nota: this.translate.instant('pessoas-table.placeholders.id_nota'),
      numero_fatura: this.translate.instant('pessoas-table.placeholders.numero_fatura'),
      emissao: this.translate.instant('pessoas-table.placeholders.emissao'),
      observacao: this.translate.instant('pessoas-table.placeholders.observacao'),
    };

    this.botoes = {
      fatura: this.translate.instant('pessoas-table.botoes.fatura'),
      buscar: this.translate.instant('pessoas-table.botoes.buscar'),
      nova_venda: this.translate.instant('pessoas-table.botoes.nova_venda'),
      nova_compra: this.translate.instant('pessoas-table.botoes.nova_compra'),
      limpar_filtros: this.translate.instant('pessoas-table.botoes.limpar_filtros'),
      filtrar: this.translate.instant('pessoas-table.botoes.filtrar'),
    };

    this.tooltips = {
      ver_fatura: this.translate.instant('pessoas-table.tooltips.ver_fatura'),
      filtros_avancados: this.translate.instant('pessoas-table.tooltips.filtros_avancados'),
    };

    // Atualizar entity-selector config com categoria, placeholder e permissao atualizados
    const permissaoTipo = this.ehCompra ? 'fornecedor' : 'cliente';
    this.filtroClienteSelectorConfig = {
      ...this.filtroClienteSelectorConfig,
      categoryFilter: this.filtroCategoriaEntitySelector,
      placeholder: this.placeholders.selecionar_cliente,
      showAddInDropdown: this.permissaoService.temPermissao(permissaoTipo),
    };
  }

  // =========================================================================
  // Filtros Avancados - Metodos
  // =========================================================================

  // =========================================================================
  // Entity Selector - Callbacks de cliente no filtro
  // =========================================================================

  /** Chamado quando o usuario seleciona um cliente no entity-selector do filtro */
  onFiltroPessoaSelecionada(pessoa: PessoaDTO): void {
    this.filtroClienteSelecionado = pessoa;
  }

  /** Abre o dialog de adicionar novo cliente (acionado pelo "+ Agregar" do entity-selector) */
  abrirDialogoCliente(): void {
    this.visibleDialogCliente = true;
  }

  /** Callback quando um novo cliente e criado via dialog */
  onClienteCriado(pessoa: PessoaDTO): void {
    this.filtroClienteSelecionado = pessoa;
    this.visibleDialogCliente = false;
  }

  /**
   * Monta o VendaFiltrosDTO a partir dos campos da sidebar e executa busca.
   * Chamado ao clicar "Filtrar" na sidebar.
   */
  aplicarFiltrosAvancados(): void {
    this.montarFiltrosDTO();
    this.filtrosAtivos = true;
    this.first = 0;
    this.sidebarFiltrosVisible = false;
    this.executarBusca();
  }

  /**
   * Monta o VendaFiltrosDTO a partir dos campos da sidebar.
   */
  private montarFiltrosDTO(): void {
    this.filtros.notaId = this.filtroNotaId ? Number(this.filtroNotaId) : undefined;
    this.filtros.numeroFatura = this.filtroNumeroFatura ? Number(this.filtroNumeroFatura) : undefined;
    this.filtros.pessoaId = this.filtroClienteSelecionado?.id;
    this.filtros.vendedorId = this.filtroVendedorSelecionado?.id;
    this.filtros.observacao = this.filtroObservacao || undefined;

    // Situacao: 'todos' nao envia o parametro, e o backend devolve vigentes e canceladas.
    this.filtros.cancelado = this.filtroCanceladoOpcao === 'todos'
      ? undefined
      : this.filtroCanceladoOpcao === 'sim';

    this.filtros.ehFaturaLegal = this.filtroFaturaLegalOpcao === 'todos'
      ? undefined
      : this.filtroFaturaLegalOpcao === 'sim';

    // Emissao: converter Date para string formato dd/MM/yyyy HH:mm:ss
    if (this.filtroDataEmissao) {
      const d = this.filtroDataEmissao;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      this.filtros.dataHoraInicial = `${day}/${month}/${year} 00:00:00`;
      this.filtros.dataHoraFinal = `${day}/${month}/${year} 23:59:59`;
    } else {
      this.filtros.dataHoraInicial = undefined;
      this.filtros.dataHoraFinal = undefined;
    }
  }

  /**
   * Reseta todos os filtros da sidebar para valores padrao.
   */
  limparFiltrosAvancados(): void {
    this.filtros = {};
    this.filtroNotaId = '';
    this.filtroNumeroFatura = '';
    this.filtroDataEmissao = undefined;
    this.filtroObservacao = '';
    this.filtroClienteSelecionado = undefined;
    this.filtroVendedorSelecionado = undefined;
    this.filtroCanceladoOpcao = 'todos';
    this.filtroFaturaLegalOpcao = 'todos';
    this.filtrosAtivos = false;
    this.first = 0;
    this.sidebarFiltrosVisible = false;
    this.executarBusca();
  }

  /**
   * Metodo unificado de busca.
   * Se filtrosAtivos === true, usa buscarComFiltrosAvancados do service.
   * Caso contrario, usa o fluxo original (findNota).
   */
  private executarBusca(): void {
    const sort = `${this.lastfield},${this.lastSort}`;
    const page = this.first / this.rows;

    if (this.mostrarFiltrosAvancados && this.filtrosAtivos) {
      this.loading = true;
      this.notaService.buscarComFiltrosAvancados(
        this.filtros,
        String(this.tipoTransacao),
        page,
        this.rows,
        sort,
        this.search
      ).subscribe({
        next: (response) => {
          this.notas = response.content;
          this.totalRecords = response.totalElements;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar notas com filtros:', error);
          this.notas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
    } else {
      this.findNota(page, this.rows, sort);
    }
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    if (this.pessoaId && (key === 'nomePessoa' || key === 'nomeVendedor')) {
      return false;
    }
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    let count = this.columns.filter(c => c.visible).length;
    if (this.pessoaId) {
      count -= 2;
    }
    return count;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Busca
  // =========================================================================

  private resolveEnumSearch(searchTerm: string, searchMap: [string, string][]): string {
    if (!searchTerm || searchTerm.trim().length === 0) return searchTerm;

    const normalized = searchTerm.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    for (const [label, enumValue] of searchMap) {
      if (normalized.includes(label) || label.includes(normalized)) {
        return enumValue;
      }
    }

    return searchTerm;
  }

  onSearchChange(value: string): void {
    this.search = value || '';
    if (this.search.length >= 3 || this.search.length === 0) {
      this.first = 0;
      this.searchNota();
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.searchNota();
    }
  }

  searchNota(): void {
    if (this.mostrarFiltrosAvancados && this.filtrosAtivos) {
      this.first = 0;
      this.executarBusca();
    } else {
      this.findNota(0, this.rows, `${this.lastfield},${this.lastSort}`);
    }
  }

  /**
   * Disparado a cada alteracao do ngModel do p-calendar em modo range.
   *
   * Fonte unica de verdade para detectar limpeza do filtro, cobrindo:
   *  - X do input (showClear)
   *  - Botao "Clear" do buttonBar (showButtonBar) - que NAO emite (onClear)
   *  - Limpeza programatica do rangeDates
   *
   * Regras:
   *  - Se o valor virou "vazio" (null/undefined/array vazio/ambas posicoes
   *    null-undefined) E rangeDates ANTERIOR ja tinha algum valor, limpa e
   *    recarrega a lista. Ignoramos o caso "vazio -> vazio" para nao disparar
   *    HTTP duplicado no bootstrap ou em cliques repetidos no Clear.
   *  - Se e range parcial ([inicio, null]), nao faz nada (aguarda o fim).
   *  - Se e range completo ([inicio, fim]), nao faz nada aqui: o (onSelect)
   *    cuida disso via onSelectData().
   */
  onRangeDatesChange(value: Date[] | null | undefined): void {
    const anteriorTinhaValor = !!(this.rangeDates && this.rangeDates.some(d => d != null));
    const novoEstaVazio = !value || value.length === 0 || value.every(d => d == null);

    if (novoEstaVazio) {
      // Atualiza o estado local (caso ainda nao tenha sido refletido).
      this.rangeDates = null;
      // So recarrega se realmente houve uma transicao de "tinha valor" -> "vazio".
      if (anteriorTinhaValor) {
        this.first = 0;
        this.searchNota();
      }
      return;
    }

    // Range parcial ou completo: apenas mantem o ngModel; a busca efetiva
    // ocorre em onSelectData() quando o range estiver completo.
    this.rangeDates = value;
  }

  /**
   * Disparado a cada selecao no p-calendar em modo range. So dispara a
   * busca quando o usuario ja escolheu data inicial E final.
   */
  onSelectData(): void {
    const inicio = this.rangeDates?.[0];
    const fim = this.rangeDates?.[1];
    if (inicio && fim) {
      this.first = 0;
      this.searchNota();
    }
  }

  // =========================================================================
  // Tabela
  // =========================================================================

  findNota(
    page: number = 0,
    size: number = this.rows,
    sort: string = 'id,asc'
  ): Promise<void> {
    return new Promise((resolve) => {
      this.loading = true;
      let filter = '';

      if (!isNaN(Number(this.search))) {
        filter += `&codigoNota=${this.search}`;
      }

      filter += `&tipoDeTransacao=${this.tipoTransacao}`;
      filter += `&nomePessoa=${this.search}`;
      filter += `&nomeVendedor=${this.search}`;
      filter += `&condicaoDePagamento=${this.resolveEnumSearch(this.search, this.condicaoSearchMap)}`;
      filter += `&estadoNota=${this.resolveEnumSearch(this.search, this.estadoSearchMap)}`;
      filter += `&tipoNota=${this.resolveEnumSearch(this.search, this.tipoNotaSearchMap)}`;

      // Filtro por pessoa quando vindo de acoes rapidas de clientes
      if (this.pessoaId) {
        filter += `&pessoaId=${this.pessoaId}`;
      }

      // Datas: usar rangeDates do header (para todos os tipos).
      // rangeDates e um array [inicio, fim]. Enquanto o usuario ainda nao
      // escolheu a data final, o segundo item pode vir como null.
      const inicio = this.rangeDates?.[0];
      const fim = this.rangeDates?.[1];

      if (inicio) {
        filter += `&dataInicio=${this.formatDate(inicio)}`;
      }
      if (fim) {
        filter += `&dataFim=${this.formatDate(fim)}`;
      }

      this.notaService
        .get('/nota/buscar', { page, size, sort, filter })
        .subscribe(
          (response) => {
            this.notas = response.content;
            this.totalRecords = response.totalElements;
            this.first = page * size;
            this.loading = false;
            resolve();
          },
          (error) => {
            console.error('Erro ao buscar notas:', error);
            this.notas = [];
            this.totalRecords = 0;
            // Nao limpar rangeDates aqui: o usuario deve poder ajustar o
            // periodo selecionado mesmo quando a busca retorna vazia/erro.
            this.loading = false;
          }
        );
    });
  }

  onPageChange(event: any): void {
    const page = event.page;
    const size = event.rows;
    if (this.mostrarFiltrosAvancados && this.filtrosAtivos) {
      this.first = page * size;
      this.rows = size;
      this.executarBusca();
    } else {
      this.findNota(page, size, `${this.lastfield},${this.lastSort}`);
    }
  }

  onRowsPerPageChange(): void {
    // Guarda: o (ngModelChange) do p-dropdown de itens por pagina emite no
    // bootstrap com o valor atual, o que causaria uma chamada HTTP
    // duplicada de findNota alem da feita em ngOnInit. Ignorar quando
    // "rows" nao mudou de valor.
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    if (this.mostrarFiltrosAvancados && this.filtrosAtivos) {
      this.executarBusca();
    } else {
      this.findNota(0, this.rows, `${this.lastfield},${this.lastSort}`);
    }
  }

  onSortChange(event: { field: string; order: number }): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evita loop: a p-table reemite (onSort) quando [value] é reatribuído.
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;
    this.first = 0;
    this.executarBusca();
  }

  // =========================================================================
  // Acoes
  // =========================================================================

  novaAcao(): void {
    if (this.actionRoute) {
      this.router.navigate([this.actionRoute]);
    }
  }

  visualizarFatura(id: number): void {
    this.router.navigate(['/notas-list/fatura', id]);
  }

  // =========================================================================
  // Formatacao
  // =========================================================================

  formatDate(dateString: string | Date): string {
    if (!dateString) return '-';
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-4);
    return `${day}/${month}/${year}`;
  }

  formatDateHour(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-4);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  formatarValorMonetario(valor: number, moeda: Moeda | any): string {
    if (valor == null || isNaN(valor)) return '-';

    const configuracoesMoeda: Record<Moeda, { locale: string; simbolo: string }> = {
      [Moeda.GUARANI]: { locale: 'es-PY', simbolo: 'Gs' },
      [Moeda.REAL]: { locale: 'pt-BR', simbolo: 'R$' },
      [Moeda.DOLAR]: { locale: 'en-US', simbolo: '$' },
      [Moeda.PESO]: { locale: 'es-AR', simbolo: '$' },
    };

    // Extrair o valor enum string quando moeda vem como objeto do backend (LocalizableEnumSerializer)
    const moedaEnum: Moeda = (moeda as any)?.enum ?? moeda;

    const { locale, simbolo } = configuracoesMoeda[moedaEnum] ?? configuracoesMoeda[Moeda.GUARANI];

    const isGuarani = moedaEnum === Moeda.GUARANI;

    const options: Intl.NumberFormatOptions = isGuarani
      ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

    const valorFormatado = new Intl.NumberFormat(locale, options).format(valor);

    return `${simbolo} ${valorFormatado}`;
  }

  formatarNumeroTimbrado(timbrado?: TimbradoDTO, numeroFatura?: string): string {
    if (!timbrado) return '';

    const numeroFilialFormatado = String(timbrado.numeroFilial).padStart(3, '0');
    const pontoDeExpedicaoFormatado = String(timbrado.pontoDeExpedicao).padStart(3, '0');
    const numeroFaturaFormatado = String(numeroFatura).padStart(7, '0');

    return `${numeroFilialFormatado}-${pontoDeExpedicaoFormatado}-${numeroFaturaFormatado}`;
  }

  formatarNumeroFaturaCompra(nota: NotaDTO): string {
    if (!nota.numeroFilial || !nota.numeroPontoExpedicao || !nota.numeroFatura) {
      return '';
    }

    const numeroFilialFormatado = String(nota.numeroFilial).padStart(3, '0');
    const pontoDeExpedicaoFormatado = String(nota.numeroPontoExpedicao).padStart(3, '0');
    const numeroFaturaFormatado = String(nota.numeroFatura).padStart(7, '0');

    return `${numeroFilialFormatado}-${pontoDeExpedicaoFormatado}-${numeroFaturaFormatado}`;
  }

  /**
   * Callback chamado pelo componente de periodo-filtro quando o usuario altera o periodo.
   * Atualiza as datas nos filtros e o texto descritivo do periodo.
   */
  onPeriodoChanged(periodo: PeriodoFiltro): void {
    this.filtros = {
      ...this.filtros,
      dataHoraInicial: periodo.dataInicio,
      dataHoraFinal: periodo.dataFim,
    };
    this.periodoTexto = periodo.texto;
  }

}
